// eslint-disable-next-line no-unused-vars
import { ServicesProviderPricingDelivery } from '@ekhodealer/ekho-common';

/**
 * Calculate delivery price for linear pricing model (price per mile)
 * @param distance - Distance in miles
 * @param pricePerMile - Price per mile in dollars
 * @returns Total delivery price
 */
export function calculateLinearDeliveryPrice(distance: number, pricePerMile: number): number {
	if (!distance || distance <= 0 || !pricePerMile || pricePerMile <= 0) {
		return 0;
	}
	return distance * pricePerMile;
}

/**
 * Calculate delivery price for stepped pricing model (base price for initial distance, then price per mile)
 * @param distance - Distance in miles
 * @param baseDistance - Base distance covered by flat rate
 * @param basePrice - Flat rate for base distance
 * @param pricePerMileAfter - Price per mile after base distance
 * @returns Total delivery price
 */
export function calculateSteppedDeliveryPrice(
	distance: number,
	baseDistance: number,
	basePrice: number,
	pricePerMileAfter: number,
): number {
	if (!distance || distance <= 0) {
		return 0;
	}

	// If distance is within base distance, return base price
	if (distance <= baseDistance) {
		return basePrice;
	}

	// Calculate additional miles beyond base distance
	const additionalMiles = distance - baseDistance;
	const additionalPrice = additionalMiles * pricePerMileAfter;

	return basePrice + additionalPrice;
}

/**
 * Calculate delivery price based on pricing model and configuration
 * @param distance - Distance in miles
 * @param pricingConfig - Complete pricing configuration with model and details
 * @returns Total delivery price
 * @throws Error if unsupported pricing model is provided
 */
export function calculateServiceProviderDelivery(
	distance: number,
	pricingConfig: ServicesProviderPricingDelivery,
): number {
	switch (pricingConfig.model) {
		case 'flatFee':
			return pricingConfig.modelDetails.flatFee || 0;

		case 'linear':
			return calculateLinearDeliveryPrice(
				distance,
				pricingConfig.modelDetails.pricePerMile || 0,
			);

		case 'stepped':
			return calculateSteppedDeliveryPrice(
				distance,
				pricingConfig.modelDetails.baseDistance || 0,
				pricingConfig.modelDetails.basePrice || 0,
				pricingConfig.modelDetails.pricePerMileAfter || 0,
			);

		default:
			throw new Error(`Unsupported delivery pricing model: ${pricingConfig}`);
	}
}
